import { Question, Quiz } from "@cryptors/api-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowBack, Close } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import { useNotification } from "@refinedev/core";
import { Edit } from "@refinedev/mui";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  useDeleteQuestion,
  usePatchQuestion,
  usePostQuestion,
} from "@/api/questions.api";
import { useDeleteQuiz, usePatchQuiz } from "@/api/quiz.api";
import UploadField from "@/components/forms/UploadField";
import { getTranslation } from "@/utils/translation";

import { QuestionsFormData, QuestionsSchema } from "./question.schema";

interface BaseQuestionsEditProps {
  data: Question[] | Quiz["questions"] | Quiz;
  cryptorId?: number;
  quiz?: Quiz;
}

const BaseQuestionsEdit: FC<BaseQuestionsEditProps> = ({
  data,
  cryptorId,
  quiz,
}) => {
  const navigate = useNavigate();

  const { mutateAsync: postQuestion } = usePostQuestion();
  const { mutateAsync: patchQuestion } = usePatchQuestion();
  const { mutateAsync: deleteQuestion } = useDeleteQuestion();
  const { mutateAsync: patchQuiz } = usePatchQuiz();
  const { mutateAsync: deleteQuiz } = useDeleteQuiz();
  const queryClient = useQueryClient();
  const { open: openNotification } = useNotification();
  const [isSaving, setIsSaving] = useState(false);

  const formatedData = useMemo(() => {
    const questions = Array.isArray(data) ? data : data.questions;
    return {
      reward: quiz?.reward,
      timerQuestion: quiz?.timerQuestion,
      questions: questions.map((question) => ({
        id: question.id,
        textTranslation: {
          value: getTranslation(question.textTranslation)?.value || "",
        },
        cryptorId: cryptorId || null,
        quizId: quiz?.id || null,
        reward: question.reward || 0,
        index: question.index || 0,
        propositions: question.propositions.map((proposition) => ({
          id: proposition.id,
          textTranslation: {
            value: getTranslation(proposition.textTranslation)?.value || "",
          },
          isCorrect: proposition.isCorrect,
        })),
        answerDetails: question.answerDetails
          ? {
              id: question.answerDetails.id,
              descriptionTranslation: {
                value:
                  getTranslation(question.answerDetails.descriptionTranslation)
                    ?.value || "",
              },
              image: question.answerDetails.image,
            }
          : {
              id: undefined,
              descriptionTranslation: { value: "" },
              image: null,
            },
      })),
    };
  }, [data, cryptorId, quiz]);

  const {
    reset,
    handleSubmit,
    formState: { isValid },
    control,
    watch,
    setError,
    setValue,
  } = useForm<QuestionsFormData>({
    resolver: zodResolver(QuestionsSchema),
    reValidateMode: "onChange",
    mode: "onChange",
    values: formatedData,
  });

  const questions = watch("questions");
  const reward = watch("reward");
  const timerQuestion = watch("timerQuestion");

  return (
    <Edit
      goBack={
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
      }
      isLoading={!questions}
      headerButtons={() => (
        <>
          {quiz && (
            <LoadingButton
              onClick={() =>
                deleteQuiz(quiz.id, {
                  onSuccess: () => {
                    navigate(-1);
                  },
                })
              }
            >
              <Box display="flex" gap={0.5}>
                <DeleteIcon />
                Delete quiz
              </Box>
            </LoadingButton>
          )}
        </>
      )}
      saveButtonProps={{
        onClick: handleSubmit(async (data) => {
          setIsSaving(true);

          const quizUpdate =
            quiz && "reward" in data && "timerQuestion" in data
              ? patchQuiz({
                  id: quiz.id,
                  data: {
                    reward: data.reward || 0,
                    timerQuestion: data.timerQuestion || 10,
                  },
                })
              : undefined;

          const questionUpdates = data.questions.map((question) => {
            const { id, answerDetails, ...questionWithoutId } = question;
            const answerDetailsFormated = answerDetails
              ? {
                  id: answerDetails.id,
                  descriptionTranslation: answerDetails.descriptionTranslation,
                  image:
                    answerDetails.image && answerDetails.image.url
                      ? { url: answerDetails.image.url }
                      : null,
                }
              : null;

            return id
              ? patchQuestion({
                  id,
                  data: {
                    ...questionWithoutId,
                    answerDetails: answerDetailsFormated,
                  },
                })
              : postQuestion({
                  ...questionWithoutId,
                  answerDetails: answerDetailsFormated,
                });
          });

          const results = await Promise.allSettled([
            quizUpdate,
            ...questionUpdates,
          ]);

          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["cryptors", "quiz"] }),
            queryClient.invalidateQueries({ queryKey: ["questions"] }),
          ]);
          setIsSaving(false);

          if (results.some((result) => result.status === "rejected")) {
            openNotification?.({
              type: "error",
              message: "Echec de la modification",
              description:
                "Certaines modifications n'ont pas pu être enregistrées. Veuillez réessayer",
            });
          } else {
            openNotification?.({
              type: "success",
              message: "Modification réussie",
              description: "Les modifications ont été enregistrées",
            });
          }
        }),
        disabled: !isValid || isSaving,
        loading: isSaving,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        {quiz && (
          <>
            <Controller
              control={control}
              name="reward"
              defaultValue={0}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="number"
                  label="Récompense 100% de réussite"
                  onChange={(event) =>
                    field.onChange(Number(event.target.value))
                  }
                />
              )}
            />
            <Controller
              control={control}
              name="timerQuestion"
              defaultValue={10}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="number"
                  label="Timer"
                  onChange={(event) =>
                    field.onChange(Number(event.target.value))
                  }
                />
              )}
            />
          </>
        )}
        {questions?.map((question, index) => (
          <Box key={index}>
            <Box display="flex" flexDirection="row" gap={3}>
              <Box marginY={2}>
                <IconButton
                  aria-label="delete"
                  onClick={() => {
                    const { id } = question;
                    if (id) {
                      deleteQuestion(id);
                    } else {
                      reset({
                        reward,
                        timerQuestion,
                        questions: questions.filter((el, i) => i !== index),
                      });
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Box display="flex" flexDirection="column" flex={1}>
                <Box display="flex" flexDirection="row" gap={3}>
                  <Box display="flex" flexDirection="column" flex={1}>
                    <Controller
                      name={`questions.${index}.id`}
                      control={control}
                      render={({ field }) => {
                        return (
                          <TextField
                            {...field}
                            margin="normal"
                            label="Id"
                            disabled
                          />
                        );
                      }}
                    />
                    <Controller
                      name={`questions.${index}.textTranslation.value`}
                      control={control}
                      render={({ field }) => {
                        return (
                          <TextField
                            {...field}
                            margin="normal"
                            label="Question"
                            multiline
                          />
                        );
                      }}
                    />
                    {question.propositions.map((proposition, pIndex) => {
                      return (
                        <Stack
                          key={
                            "question.propositions" + proposition.id + pIndex
                          }
                          direction="row"
                          alignItems="center"
                          gap={3}
                        >
                          <Controller
                            name={`questions.${index}.propositions.${pIndex}.textTranslation.value`}
                            control={control}
                            render={({ field }) => {
                              return (
                                <TextField
                                  {...field}
                                  margin="normal"
                                  label={`Réponse ${pIndex + 1}`}
                                  style={{ flex: 1 }}
                                />
                              );
                            }}
                          />
                          <Controller
                            control={control}
                            name={`questions.${index}.propositions.${pIndex}.isCorrect`}
                            render={({ field }) => {
                              return (
                                <FormControlLabel
                                  checked={field.value || false}
                                  onChange={field.onChange}
                                  onBlur={field.onBlur}
                                  control={<Checkbox />}
                                  label="Est correct"
                                  sx={{ minWidth: 125 }}
                                />
                              );
                            }}
                          />
                          <IconButton
                            onClick={() => {
                              setValue(
                                `questions.${index}.propositions`,
                                question.propositions.filter(
                                  (el, i) => i !== pIndex,
                                ),
                              );
                            }}
                          >
                            <Close />
                          </IconButton>
                        </Stack>
                      );
                    })}
                    <Button
                      onClick={() => {
                        setValue(`questions.${index}.propositions`, [
                          ...question.propositions,
                          {
                            id: undefined,
                            textTranslation: { value: "" },
                            isCorrect: false,
                          },
                        ]);
                      }}
                    >
                      Ajouter une proposition
                    </Button>
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <Controller
                      name={`questions.${index}.reward`}
                      control={control}
                      render={({ field }) => {
                        return (
                          <TextField
                            {...field}
                            margin="normal"
                            label="Récompense"
                            onChange={(event) =>
                              field.onChange(Number(event.target.value))
                            }
                          />
                        );
                      }}
                    />
                    <Controller
                      name={`questions.${index}.index`}
                      control={control}
                      render={({ field }) => {
                        return (
                          <TextField
                            {...field}
                            margin="normal"
                            label="Index"
                            onChange={(event) =>
                              field.onChange(Number(event.target.value))
                            }
                          />
                        );
                      }}
                    />
                  </Box>
                </Box>
                <Box display="flex" flexDirection="row" gap={3}>
                  <Box display="flex" flexDirection="column" flex={1}>
                    <Controller
                      name={`questions.${index}.answerDetails.descriptionTranslation.value`}
                      control={control}
                      render={({ field }) => {
                        return (
                          <TextField
                            {...field}
                            margin="normal"
                            label="Detail de la bonne réponse"
                            multiline
                            minRows={3}
                          />
                        );
                      }}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <UploadField
                      name={
                        `questions.${index}.answerDetails.image.url` as const
                      }
                      uploadButtonLabel="Upload image"
                      s3FoldersType="questions"
                      control={control}
                      setError={setError}
                      optional
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ marginTop: 3 }} />
          </Box>
        ))}
        <Divider sx={{ marginTop: 3 }}>
          <Button
            onClick={() => {
              reset({
                reward,
                timerQuestion,
                questions: [
                  ...questions,
                  {
                    id: null,
                    textTranslation: { value: "" },
                    cryptorId: cryptorId ? Number(cryptorId) : null,
                    reward: 0,
                    index: questions.length + 1,
                    propositions: [],
                    answerDetails: {
                      id: undefined,
                      descriptionTranslation: { value: "" },
                      image: null,
                    },
                    quizId: quiz?.id || null,
                  },
                ],
              });
            }}
          >
            Ajouter une question
          </Button>
        </Divider>
      </Box>
    </Edit>
  );
};

export default BaseQuestionsEdit;
